import { Octokit } from '@octokit/rest';
import QRCode from 'qrcode';

class GitHubService {
  constructor() {
    this.octokit = null;
    this.userInfo = null;
  }

  // GitHub OAuth 로그인 URL 생성
  getAuthUrl() {
    const clientId = process.env.REACT_APP_GITHUB_CLIENT_ID;
    const redirectUri = `${window.location.origin}/auth/github/callback`;
    const scope = 'repo';
    
    return `https://github.com/login/oauth/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&scope=${scope}`;
  }

  // Access Token으로 GitHub API 초기화
  async initializeWithToken(accessToken) {
    this.octokit = new Octokit({
      auth: accessToken,
      retry: {
        doNotRetry: ['429'] // Rate limit은 재시도하지 않음
      },
      request: {
        timeout: 10000 // 10초 타임아웃
      }
    });

    // 사용자 정보 가져오기
    try {
      const { data } = await this.octokit.rest.users.getAuthenticated();
      this.userInfo = data;
      
      return data;
    } catch (error) {
      console.error('GitHub API 에러 상세:', {
        status: error.status,
        message: error.message,
        documentation_url: error.documentation_url
      });
      throw error;
    }
  }

  // Repository 생성 또는 확인
  async ensureRepository(repoName = 'english-study-materials') {
    try {
      // 먼저 Repository가 존재하는지 확인
      const { data } = await this.octokit.rest.repos.get({
        owner: this.userInfo.login,
        repo: repoName,
      });
      
      return data;
    } catch (error) {
      if (error.status === 404) {
        // Repository가 없으면 생성
        console.log('🔨 Repository 생성 중...');
        const { data } = await this.octokit.rest.repos.createForAuthenticatedUser({
          name: repoName,
          description: '영어 학습 문항이 자동으로 배포되는 저장소',
          homepage: `https://${this.userInfo.login}.github.io/${repoName}`,
          public: true,
          auto_init: true,
        });

        // GitHub Pages 활성화
        await this.enableGitHubPages(repoName);
        
        return data;
      } else {
        throw error;
      }
    }
  }

  // GitHub Pages 활성화
  async enableGitHubPages(repoName) {
    try {
      console.log('📄 GitHub Pages 활성화 중...');
      await this.octokit.rest.repos.createPagesSite({
        owner: this.userInfo.login,
        repo: repoName,
        source: {
          branch: 'main',
          path: '/',
        },
      });
      console.log('✅ GitHub Pages 활성화 완료');
    } catch (error) {
      if (error.status === 409) {
        console.log('ℹ️ GitHub Pages가 이미 활성화되어 있습니다');
      } else {
        console.error('❌ GitHub Pages 활성화 실패:', error);
        throw error;
      }
    }
  }

  // HTML 파일 업로드 (파일 경로 지원)
  async uploadHtmlFile(htmlContent, filePath, repoName = 'english-study-materials') {
    try {
      console.log('📤 HTML 파일 업로드 중...', filePath);
      
      // Base64로 인코딩
      const content = btoa(unescape(encodeURIComponent(htmlContent)));
      
      // 파일 경로 사용 (폴더 구조 지원)
      const path = filePath;
      
      // 기존 파일이 있는지 확인
      let sha;
      try {
        const { data: existingFile } = await this.octokit.rest.repos.getContent({
          owner: this.userInfo.login,
          repo: repoName,
          path: path,
        });
        sha = existingFile.sha;
      } catch (error) {
        if (error.status !== 404) throw error;
      }

      // 파일명 추출 (폴더에서 마지막 부분)
      const fileName = filePath.split('/').pop();
      
      // 콘텐츠 타입에 따른 커밋 메시지
      const folderName = filePath.split('/')[0];
      const commitMessages = {
        'flashcards': '🎯 Add flashcard file',
        'quizzes': '📝 Add quiz file', 
        'reading': '📖 Add reading file',
        'questions': '📚 Add question file',
        'templates': '📄 Add template file'
      };
      const commitMessage = `${commitMessages[folderName] || '📁 Add file'}: ${fileName}`;

      // 파일 생성 또는 업데이트
      const { data } = await this.octokit.rest.repos.createOrUpdateFileContents({
        owner: this.userInfo.login,
        repo: repoName,
        path: path,
        message: commitMessage,
        content: content,
        ...(sha && { sha }),
      });

      console.log('✅ HTML 파일 업로드 완료');
      
      // GitHub Pages URL 생성
      const githubPagesUrl = `https://${this.userInfo.login}.github.io/${repoName}/${path}`;
      
      return {
        success: true,
        url: githubPagesUrl,
        fileName: fileName,
        filePath: filePath,
        repoName: repoName,
        commitSha: data.commit.sha,
      };
    } catch (error) {
      console.error('❌ HTML 파일 업로드 실패:', error);
      throw error;
    }
  }

  // QR 코드 생성
  async generateQRCode(url) {
    try {
      const qrCodeDataUrl = await QRCode.toDataURL(url, {
        width: 256,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      return qrCodeDataUrl;
    } catch (error) {
      console.error('❌ QR 코드 생성 실패:', error);
      throw error;
    }
  }

  // 배포 상태 확인 (GitHub Pages 활성화 확인)
  async checkDeploymentStatus(repoName = 'english-study-materials') {
    try {
      const { data } = await this.octokit.rest.repos.getPages({
        owner: this.userInfo.login,
        repo: repoName,
      });
      
      return {
        status: data.status,
        url: data.html_url,
        ready: data.status === 'built'
      };
    } catch (error) {
      if (error.status === 404) {
        return { status: 'not_found', ready: false };
      }
      throw error;
    }
  }

  // GitHub Pages 배포 완료까지 대기 (상태 체크)
  async waitForPageDeployment(repoName = 'english-study-materials', maxWaitTime = 180000) {
    const startTime = Date.now();
    const checkInterval = 5000; // 5초마다 체크
    
    console.log('⏳ GitHub Pages 배포 상태 확인 중...');
    
    while (Date.now() - startTime < maxWaitTime) {
      try {
        const status = await this.checkDeploymentStatus(repoName);
        
        if (status.ready) {
          console.log('✅ GitHub Pages 배포 완료!');
          return { ready: true, status: status.status };
        }
        
        if (status.status === 'errored') {
          console.log('❌ GitHub Pages 배포 실패');
          return { ready: false, status: 'errored', error: 'Deployment failed' };
        }
        
        console.log(`🔄 배포 상태: ${status.status}, 계속 대기 중...`);
        await new Promise(resolve => setTimeout(resolve, checkInterval));
        
      } catch (error) {
        console.log('⚠️ 상태 확인 중 에러:', error.message);
        await new Promise(resolve => setTimeout(resolve, checkInterval));
      }
    }
    
    console.log('⏰ 최대 대기 시간 초과 - 배포는 계속 진행됩니다');
    return { ready: false, status: 'timeout', message: '배포가 계속 진행 중입니다' };
  }

  // 전체 자동 배포 프로세스
  async autoDeployHtml(htmlContent, filePath) {
    try {
      console.log('🚀 자동 배포 시작...');
      
      // 1. Repository 확인/생성
      const repo = await this.ensureRepository();
      console.log('✅ Repository 준비 완료:', repo.name);
      
      // 2. HTML 파일 업로드
      const uploadResult = await this.uploadHtmlFile(htmlContent, filePath);
      
      // 3. QR 코드 생성
      const qrCode = await this.generateQRCode(uploadResult.url);
      
      console.log('🎉 자동 배포 완료!');
      
      return {
        ...uploadResult,
        qrCode,
        deployedAt: new Date().toISOString(),
      };
    } catch (error) {
      console.error('❌자동 배포 실패:', error);
      throw error;
    }
  }

  // 로그아웃
  logout() {
    this.octokit = null;
    this.userInfo = null;
    localStorage.removeItem('github_access_token');
  }

  // 로그인 상태 확인
  isLoggedIn() {
    return this.octokit !== null && this.userInfo !== null;
  }

  // 사용자 정보 반환
  getUserInfo() {
    return this.userInfo;
  }
}

// 싱글톤 인스턴스
const githubService = new GitHubService();
export default githubService;